import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import adminService from '../../services/api/adminService';

const ModulesPage = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [newStatus, setNewStatus] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllModules();
      setModules(data);
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Error loading modules');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (module) => {
    setSelectedModule(module);
    setNewStatus(!module.isEnabled);
    setConfirmDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    try {
      setError(null);
      await adminService.updateModuleStatus(selectedModule.id, { isEnabled: newStatus });
      setConfirmDialogOpen(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      fetchModules();
    } catch (error) {
      console.error('Error updating module status:', error);
      setError(error.response?.data?.message || 'Error updating module status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" component="h1" gutterBottom>
        Module Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Module status updated successfully!
        </Alert>
      )}

      <Paper sx={{ mt: 2, p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Enable or disable system modules. System modules are core features that cannot be removed.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Module Name</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enabled At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {module.moduleName}
                      </Typography>
                    </TableCell>
                    <TableCell>{module.displayName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {module.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {module.version ? (
                        <Chip label={module.version} size="small" />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={module.isSystemModule ? 'System' : 'Custom'}
                        color={module.isSystemModule ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={module.isEnabled ? 'Enabled' : 'Disabled'}
                        color={module.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {module.enabledAt ? formatDate(module.enabledAt) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={module.isEnabled}
                            onChange={() => handleStatusChange(module)}
                            color="primary"
                          />
                        }
                        label={module.isEnabled ? 'Enabled' : 'Disabled'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {newStatus ? 'Enable' : 'Disable'} Module
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {newStatus ? 'enable' : 'disable'} the module{' '}
            <strong>{selectedModule?.displayName}</strong>?
          </Typography>
          {selectedModule?.isSystemModule && !newStatus && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is a system module. Disabling it may affect core functionality.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmStatusChange} variant="contained" color={newStatus ? 'success' : 'error'}>
            {newStatus ? 'Enable' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ModulesPage;

